/**
 * Pure render-plan synthesizer — decides how each row's cell should
 * re-render in a given column.
 *
 * Ported from `tento/tento-lona-js/src/render-plan.ts` (Phase 2 / 2b). Output
 * shape is byte-identical to lona-js's `RenderPlan[]`; snapshot
 * replay (the repository's render-plan snapshots) pins the
 * behaviour across the port boundary.
 *
 * Type surgery from the port:
 *   - `SheetCell.Layout` (yuzu-ui) → `CellLayout` (`./layout-types`).
 *   - `SheetCell.SpanMode` (yuzu-ui) → `RenderPlan.SpanMode` (local).
 *   - `ColumnIndex.Key.Resolved.Default` (yuzu-ui) → `ColumnKey`
 *     (`./layout-types`).
 *   - `SheetRow.Joined.Handle` (lona-js) → `RenderPlan.Row` (local),
 *     a 4-field interface that yuzu-ui's rich row satisfies
 *     structurally.
 *   - `ColumnIndex.Mapper` (yuzu-ui) → `RenderPlan.Ports` (local) —
 *     the only mapper use is a flight-timezone duplicate-date test;
 *     callers adapt their real mapper to `isDuplicateDate(date)`.
 *   - `./timescale-utils` (lona-js) → `./timescale` (tento/tento-lona-js/sheets).
 */

import type { Option } from "../core/option.ts";
import type { DateTime, NaiveDate, Utc } from "@tento-chrono";
import { nonnull } from "@tento-core/assert";

import type { CellLayout, ColumnKey, LayoutSpec, SheetLayoutSpec, Timescale } from "./layout-types.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { RowRenderMode } from "./timescale.ts";
import { elementAt } from "../core/checked-index.ts";

/**
 * Render plan for a single row in a single column.
 *
 * Generic over `K extends ColumnKey` so consumers holding a
 * wider concrete column-key type (e.g. yuzu-ui's
 * `ColumnIndex.Key.Resolved.Default`) get their full type
 * surfaced through `stacking.subColumns`. Defaults to the
 * narrow `ColumnKey` for standalone rows-side usage. Mirrors
 * the `SheetViewModel<R, L>` precedent for the same
 * narrow-vs-wide structural-typing problem.
 */
export interface RenderPlan<K extends ColumnKey = ColumnKey> {
  rowId: RowLocalId;
  /** Canonical descriptor identity — the role-based `(layout)(?.(renderer))` id. */
  descriptorType: string;
  rowIndex: number;
  updateType: RenderPlan.UpdateType;
  diff: Option<RenderPlan.Diff>;
  mode: RowRenderMode;
  timeState: RenderPlan.TimeState;
  layout: Option<CellLayout>;
  spanMode: RenderPlan.SpanMode;
  skipContent: boolean;
  stacking: Option<{
    subColumns: K[];
  }>;
}

/**
 * What a render pass over a column is for: `"full"` (bind the column's
 * content) or `"resize-only"` (only its width changed: re-lay the cells it
 * already shows). One spelling for the plan (`RenderPlan.makeForRows`), a
 * binder's pass (`SheetBinder.Pass.update`) and the CLI renderer
 * (`RenderSheetTuiOptions.update`).
 */
export type RenderPass = "full" | "resize-only";

/**
 * Types and values that go with `RenderPlan`: `UpdateType`, `SpanMode`, `Diff`,
 * `TimeState`, `PreviousState`, `LayoutProvider`, and 7 more.
 */
export namespace RenderPlan {
  // ── Subtypes ─────────────────────────────────────────────────────

  export type UpdateType =
    | "none"
    | "style-only"
    | "content"
    | "full-rebuild";

  /**
   * Whether the cell renders its own content or is subsumed under a
   * spanning owner. Mirrors yuzu-ui's `SheetCell.SpanMode` shape —
   * the port lifts the variants out so the pure render-plan math
   * doesn't depend on yuzu-ui.
   */
  export type SpanMode = { type: "none" } | { type: "hidden" };

  export interface Diff {
    modeChanged: boolean;
    layoutChanged: boolean;
    timeStateChanged: boolean;
    spanModeChanged: boolean;
  }

  export interface TimeState {
    inThePast: boolean;
    isCurrent: boolean;
    isFuture: boolean;
  }

  /** Previous render state for a cell — tracked by the binder. */
  export interface PreviousState {
    descriptorType: string;
    isStacked: boolean;
    cellCount: number;
    isNew: boolean;
  }

  /** Minimal layout provider — satisfied by yuzu-ui's `SheetViewModel`. */
  export interface LayoutProvider {
    timescale: Timescale;
    layout: SheetLayoutSpec<RowLocalId>;
  }

  /**
   * Narrow row interface — just the four fields `makeForRows`
   * reads. yuzu-ui's `SheetRow.Joined.Handle` satisfies this
   * structurally (without `any` casts). tento/tento-lona-js/sheets will grow
   * its own implementation as Phase 3 populates `ROW_TYPE_SPECS`.
   */
  export interface Row {
    readonly id: RowLocalId;
    /** Canonical descriptor identity (`RenderPluginSpec.descriptorType`). */
    readonly descriptorType: string;
    readonly timescaleSetting: Timescale | "custom";
    layoutSpec(sheetTimescale: Timescale): LayoutSpec;
  }

  /**
   * External behaviours the port consults beyond `Context`'s data
   * fields. The only runtime question is "does this column date
   * resolve to the same offset at both edges?" — a flight-timezone
   * duplicate-date detector. yuzu-ui's `ColumnIndex.Mapper` adapts
   * via:
   *
   *   isDuplicateDate: (date) =>
   *     mapper.resolveKeyToDefault(ColumnIndex.Key.fromDate(date, "lower"), "lower").offset !==
   *     mapper.resolveKeyToDefault(ColumnIndex.Key.fromDate(date, "upper"), "upper").offset
   */
  export interface Ports {
    isDuplicateDate(date: NaiveDate): boolean;
  }

  export interface Context<K extends ColumnKey = ColumnKey> {
    /** Full semantic row index when plans cover a viewport selection; omitted uses input order. */
    rowIndexOf?: (id: RowLocalId) => number;
    sheet: LayoutProvider;
    column: K;
    widthPx: number;
    now: DateTime<Utc>;
    visibleWidthPx: number;
    resolvedWidthPx: number;
    ports: Ports;
    getColumnRangeWidthPx: (start: number, end: number) => number;
  }

  export interface SubcolumnCache<K extends ColumnKey = ColumnKey> {
    getOrGenerate: (
      col: K,
      timescale: Timescale,
      count: number,
    ) => K[];
  }

  // ── Functions ────────────────────────────────────────────────────

  export function timeState(
    now: DateTime<Utc>,
    columnRange: DateTime.Range<Utc>,
  ): TimeState {
    const nowBeforeStart = now.isBefore(columnRange.start);
    const containsNow = columnRange.containsExclusiveEnd(now);
    return {
      inThePast: !nowBeforeStart && !containsNow,
      isCurrent: containsNow,
      isFuture: nowBeforeStart,
    };
  }

  export function shouldGenerateSubcolumns(
    layout: Option<CellLayout>,
    timescaleSetting: Timescale | "custom",
  ): boolean {
    return layout?.stackingInfo != null && timescaleSetting !== "custom";
  }

  /**
   * Compute render plans for all rows in a column.
   * Pure function — takes previous render state, produces new plans.
   * The binder tracks previous state and owns change detection.
   */
  export function makeForRows<K extends ColumnKey = ColumnKey>(
    context: Context<K>,
    rows: Row[],
    previousState: Map<RowLocalId, PreviousState>,
    pass: RenderPass,
    subcolumnCache: SubcolumnCache<K>,
  ): RenderPlan<K>[] {
    const { sheet, column, now, visibleWidthPx, resolvedWidthPx, ports } =
      context;
    const sheetTimescale = sheet.timescale;
    const plans: RenderPlan<K>[] = [];
    const ts = timeState(now, column.range);

    // Check once per column whether this date has duplicate columns (flight sheets)
    const isDuplicateDate = ports.isDuplicateDate(column.pd.start);

    for (let idx = 0; idx < rows.length; idx++) {
      const row = elementAt(rows, idx, "render-plan: rows");
      const layout = sheet.layout.rowLayout.get(row.id) ?? null;

      const spec = row.layoutSpec(sheetTimescale);
      const hasStackingInfo = layout?.stackingInfo != null;

      const mode = RowRenderMode.compute(
        row.timescaleSetting,
        sheetTimescale,
        { spannable: spec.spannable, stackable: hasStackingInfo },
        visibleWidthPx,
        resolvedWidthPx,
        spec.minWidthTimescale,
      );

      let { spanMode, skipContent } = resolveSpanMode(mode);

      // Hide cells under duplicate-date spans (flight-timezone sheets)
      if (
        isDuplicateDate &&
        mode.type === "normal" &&
        spec.spannable &&
        row.timescaleSetting === sheetTimescale
      ) {
        spanMode = { type: "hidden" };
        skipContent = true;
      }

      // The row's own timescale; a "custom" row is never stacked.
      const rowTimescale = row.timescaleSetting !== "custom" ? row.timescaleSetting : null;
      const isStacked = layout?.stackingInfo != null && rowTimescale != null;
      const cellCount = isStacked
        ? nonnull(layout?.stackingInfo, "render-plan: stacked layout").cellsPerColumn
        : 0;
      let stacking: RenderPlan<K>["stacking"] = null;
      if (!skipContent && rowTimescale != null && isStacked) {
        const subColumns = subcolumnCache.getOrGenerate(
          column,
          rowTimescale,
          cellCount,
        );
        stacking = { subColumns };
      }

      const prev = previousState.get(row.id) ?? null;
      const { updateType, diff } = resolveUpdateType(
        prev,
        pass,
        skipContent,
        row.descriptorType,
        isStacked,
        cellCount,
      );

      plans.push({
        rowId: row.id,
        descriptorType: row.descriptorType,
        rowIndex: context.rowIndexOf?.(row.id) ?? idx,
        updateType,
        diff,
        mode,
        timeState: ts,
        layout,
        spanMode,
        skipContent,
        stacking,
      });
    }

    return plans;
  }
}

// ── Internal helpers ───────────────────────────────────────────────

function resolveSpanMode(
  mode: RowRenderMode,
): { spanMode: RenderPlan.SpanMode; skipContent: boolean } {
  if (mode.type === "spanned" || mode.type === "span-stacked") {
    return { spanMode: { type: "hidden" }, skipContent: true };
  }
  return { spanMode: { type: "none" }, skipContent: false };
}

function resolveUpdateType(
  prev: Option<RenderPlan.PreviousState>,
  pass: RenderPass,
  skipContent: boolean,
  descriptorType: string,
  isStacked: boolean,
  cellCount: number,
): { updateType: RenderPlan.UpdateType; diff: Option<RenderPlan.Diff> } {
  if (!prev || prev.isNew) {
    return {
      updateType: "full-rebuild",
      diff: {
        modeChanged: true,
        layoutChanged: true,
        timeStateChanged: true,
        spanModeChanged: true,
      },
    };
  }

  if (skipContent) {
    return { updateType: "style-only", diff: null };
  }

  if (pass === "resize-only") {
    return { updateType: "style-only", diff: null };
  }

  if (prev.descriptorType !== descriptorType) {
    return {
      updateType: "full-rebuild",
      diff: {
        modeChanged: true,
        layoutChanged: false,
        timeStateChanged: false,
        spanModeChanged: false,
      },
    };
  }

  if (prev.isStacked !== isStacked || prev.cellCount !== cellCount) {
    return {
      updateType: "full-rebuild",
      diff: {
        modeChanged: false,
        layoutChanged: true,
        timeStateChanged: false,
        spanModeChanged: false,
      },
    };
  }

  return { updateType: "content", diff: null };
}
